import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Loading from 'components/Loading';
import useStyles from './TasksWidget.styled';
import { useHistory } from 'react-router-dom';
import { Card, ItemsList } from 'components/UI';
import { TasksActions } from 'state/modules/tasks';
import { CreateEditTaskModal } from 'components/Modals';
import { Box, Button, Typography } from '@material-ui/core';

const TaskWidget = ({ id, type, viewAll }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [openCreateTaskModal, setOpenTaskModal] = useState(false);
  const [params, setParams] = useState({
    page: 1,
    limit: 5,
  });
  const { loading, list, pagination, fetchedCreateTask } = useSelector(
    (state) => state.tasks || {}
  );

  /**
   * Get comments
   * @param {object} params
   */
  const getTasks = (params) => {
    const payload = {
      statuses: 'ALL_ACTIVE',
      ...params,
    };
    if (id) {
      payload.object_id = id;
    }
    if (type) {
      payload.object_class = type;
    }
    dispatch(TasksActions.fetchTasks(payload));
  };

  const handleOpenCreateTaskModal = () => setOpenTaskModal((prevValue) => !prevValue);

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handleChangePage = (event, newPage) => {
    const newParams = { ...params, page: newPage + 1 };
    setParams(newParams);
    getTasks(newParams);
  };

  /**
   * Handle limit / page size change
   * @param {object} event Value of the input
   */
  const handleChangeRowsPerPage = (event) => {
    const newParams = { ...params, limit: event.target.value };
    setParams(newParams);
    getTasks(newParams);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('tasks.create'),
      can: { action: 'create', name: 'task' },
      onClick: handleOpenCreateTaskModal,
    },
  ];

  const title = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="task-title">
          {i18n.t('tasks.title')}
        </Box>
      </Typography>
      <Button
        type="button"
        variant="text"
        color="primary"
        data-cy="view-all-tasks"
        disableFocusRipple={true}
        className={classes.button}
        onClick={() => {
          let route = '';
          if (viewAll === 'all') route = '/tasks';
          else route = `/tasks?object_id=${id}`;

          history.push(buildUrl(route));
        }}
      >
        {i18n.t('tasks.view_all')}
      </Button>
    </Box>
  );

  useEffect(() => {
    getTasks(params);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (fetchedCreateTask) {
      getTasks(params);
    }
  }, [fetchedCreateTask]); // eslint-disable-line

  return (
    <Card title={title} menuItems={items} data-cy="task-card" headerActionType="vertical">
      <Box className={classes.container}>
        {loading ? (
          <Box
            className={classes.loadingContainer}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Loading />
          </Box>
        ) : (
          <ItemsList
            list={list}
            params={params}
            pagination={pagination}
            footerDirection="column"
            rowsPerPageOptions={[5]}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            placeholder={i18n.t('tasks.tasks_placeholder')}
            handleItemClick={(id) => history.push(buildUrl(`/tasks/${id}`))}
          />
        )}
        {openCreateTaskModal && (
          <CreateEditTaskModal
            id={id}
            type={type}
            open={openCreateTaskModal}
            handleClick={handleOpenCreateTaskModal}
          />
        )}
      </Box>
    </Card>
  );
};

TaskWidget.propTypes = {
  id: PropTypes.number,
  type: PropTypes.string,
  viewAll: PropTypes.string,
};

export default TaskWidget;
